import React, { useState } from 'react';
import { AlertTriangle, Check, FileText, Mail, Phone } from 'lucide-react';

/**
 * Review what the parser read, before anything is scored.
 *
 * Uploading used to parse, create and score in one go, so the first time a
 * recruiter saw a candidate's details was after the AI had already judged them
 * — and a missed email meant a candidate nobody could contact, discovered
 * later. This holds the batch at the parsed stage: every file is shown with
 * the contact details it produced, missing ones are called out, and scoring
 * only starts once the person uploading has confirmed.
 */

const MUTED = 'var(--text-muted)';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// What a phone number may contain: digits, and the punctuation people actually
// write them with. Checked BEFORE counting digits — stripping non-digits first
// would quietly accept "23y4iy234y298349" as a valid number.
// `+` is permitted anywhere here so a misplaced one gets its own message
// rather than being reported as a stray letter.
const PHONE_ALLOWED_RE = /^[+\d\s().-]+$/;

/**
 * Drop anything that cannot belong in a phone number, as it is typed.
 *
 * Rejecting after the fact let someone fill the box with letters and only find
 * out at Confirm. Filtering on the way in means the character never lands, so
 * pasting a mangled string leaves just the usable digits behind.
 */
export const PHONE_MAX_DIGITS = 15;   // E.164 allows at most 15

export function sanitizePhone(value) {
  const cleaned = String(value ?? '').replace(/[^+\d\s().-]/g, '');
  // A leading + is meaningful; anywhere else it is a typo.
  const lead = cleaned.startsWith('+') ? '+' : '';
  const rest = cleaned.replace(/\+/g, '');

  // Stop at 15 digits rather than letting them run on and fail at Confirm.
  // Formatting characters pass through freely; only digits count.
  let digits = 0;
  let out = '';
  for (const ch of rest) {
    if (ch >= '0' && ch <= '9') {
      if (digits >= PHONE_MAX_DIGITS) break;
      digits += 1;
    }
    out += ch;
  }
  return lead + out;
}

export function phoneError(value) {
  const phone = String(value || '').trim();
  if (!phone) return null;
  if (!PHONE_ALLOWED_RE.test(phone)) return 'Digits only — letters are not allowed';
  if (phone.lastIndexOf('+') > 0) return 'A + may only appear at the start';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 7) return `Too short — ${digits.length} digit${digits.length === 1 ? '' : 's'}`;
  if (digits.length > 15) return `Too long — ${digits.length} digits, maximum is 15`;
  return null;
}

export function validateRow(row) {
  const errors = {};
  if (!String(row.name || '').trim()) errors.name = 'Name is required';

  const email = String(row.email || '').trim();
  if (!email) errors.email = 'Email is required';
  else if (!EMAIL_RE.test(email)) errors.email = 'Not a valid email address';

  const phone = phoneError(row.phone);
  if (phone) errors.phone = phone;
  return errors;
}

function Field({ label, icon, value, onChange, error, placeholder, missing, inputMode }) {
  return (
    // Grid rows rather than flow: the label was inline, so a short one like
    // "Name" sat beside its input while "Email" and "Phone" wrapped above
    // theirs, leaving the three columns out of line with each other.
    <label style={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', minWidth: 0 }}>
      <span style={{
        display: 'flex', alignItems: 'center', gap: 5, minHeight: 16,
        fontSize: 11, fontWeight: 600, letterSpacing: '.04em',
        textTransform: 'uppercase', color: error ? 'var(--red)' : MUTED,
      }}>
        {icon}{label}
        {missing && !error && (
          <span style={{ color: 'var(--orange)', textTransform: 'none', letterSpacing: 0 }}>
            · not found
          </span>
        )}
      </span>
      <input
        className="form-input"
        value={value || ''}
        placeholder={placeholder}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
        style={{
          marginTop: 4, fontSize: 13,
          // Fill the grid column, so all three boxes are the same width and
          // their left and right edges line up down the row.
          width: '100%', boxSizing: 'border-box',
          borderColor: error ? 'var(--red)' : undefined,
        }}
      />
      {error && (
        <span style={{ fontSize: 11.5, color: 'var(--red)', marginTop: 3, display: 'block' }}>
          {error}
        </span>
      )}
    </label>
  );
}

export default function ResumeReviewModal({ rows, skipped, onCancel, onConfirm, saving }) {
  const [edited, setEdited] = useState(rows);
  const [touched, setTouched] = useState(false);
  // Fields the user has typed in. A field shows its error as soon as they have
  // touched it — waiting for Confirm meant typing nonsense and getting no
  // feedback until the end.
  const [dirty, setDirty] = useState({});

  const set = (id, field, value) => {
    setDirty((d) => ({ ...d, [`${id}:${field}`]: true }));
    setEdited((list) => list.map((r) => (r.candidate_id === id ? { ...r, [field]: value } : r)));
  };

  const errorsFor = edited.map(validateRow);
  const blocking = errorsFor.some((e) => Object.keys(e).length > 0);
  const missingContact = edited.filter((r) => !r.email || !r.phone).length;

  return (
    <div className="modal-backdrop" role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, width: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column' }}
      >
        <h3 style={{ marginBottom: 4 }}>
          Check {edited.length} resume{edited.length === 1 ? '' : 's'} before scoring
        </h3>
        <div className="modal-sub" style={{ marginBottom: 4 }}>
          These are the details read from each file. Correct anything wrong — nothing is
          scored until you confirm.
        </div>

        {missingContact > 0 && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'flex-start',
            background: 'var(--yellow-bg)', border: '1px solid rgba(245,158,11,0.35)',
            borderRadius: 'var(--radius)', padding: '9px 12px', margin: '10px 0 4px',
            fontSize: 12.5, color: 'var(--text-secondary)',
          }}>
            <AlertTriangle size={14} strokeWidth={2} color="var(--orange)" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              {missingContact} of {edited.length} {missingContact === 1 ? 'resume is' : 'resumes are'} missing
              an email or phone. Without them the candidate cannot be invited to an interview.
            </span>
          </div>
        )}

        <div style={{ overflowY: 'auto', margin: '12px 0', paddingRight: 4, flex: 1 }}>
          {edited.map((row, i) => {
            // Show an error once the field has been touched, or once Confirm
            // has been attempted — whichever comes first.
            const all = errorsFor[i];
            const errs = touched
              ? all
              : Object.fromEntries(
                Object.entries(all).filter(([f]) => dirty[`${row.candidate_id}:${f}`]),
              );
            return (
              <div
                key={row.candidate_id}
                style={{
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                  padding: 14, marginBottom: 10, background: 'var(--bg-card)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <FileText size={14} strokeWidth={1.9} color={MUTED} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: MUTED, wordBreak: 'break-all' }}>{row.filename}</span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                  gap: 12,
                  // Top-align, so a field carrying an error message does not
                  // drag its neighbours' inputs down with it.
                  alignItems: 'start',
                }}>
                  <Field
                    label="Name" value={row.name} error={errs.name}
                    placeholder="Full name"
                    onChange={(v) => set(row.candidate_id, 'name', v)}
                  />
                  <Field
                    label="Email" icon={<Mail size={11} strokeWidth={2.2} />}
                    value={row.email} error={errs.email} missing={!row.email}
                    placeholder="name@example.com"
                    onChange={(v) => set(row.candidate_id, 'email', v)}
                  />
                  <Field
                    label="Phone" icon={<Phone size={11} strokeWidth={2.2} />}
                    value={row.phone} error={errs.phone} missing={!row.phone}
                    placeholder="+91 98450 11223"
                    inputMode="tel"
                    onChange={(v) => set(row.candidate_id, 'phone', sanitizePhone(v))}
                  />
                </div>

                {/* Read-only context, so the details above can be judged against
                    the rest of what the parser found. Only rendered when the
                    parser actually found something — an empty strip of dashes
                    tells the reader nothing. */}
                {(row.current_role || row.current_company || row.location
                  || row.total_experience_years != null) && (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '4px 14px',
                    marginTop: 11, paddingTop: 10, borderTop: '1px solid var(--border)',
                    fontSize: 12, color: MUTED,
                  }}>
                    {[row.current_role, row.current_company].filter(Boolean).length > 0 && (
                      <span>{[row.current_role, row.current_company].filter(Boolean).join(' · ')}</span>
                    )}
                    {row.total_experience_years != null && <span>{row.total_experience_years} yrs</span>}
                    {row.location && <span>{row.location}</span>}
                  </div>
                )}
              </div>
            );
          })}

          {skipped.length > 0 && (
            <div style={{
              border: '1px dashed var(--border-light)', borderRadius: 'var(--radius)',
              padding: '11px 14px', fontSize: 12.5, color: MUTED,
            }}>
              <strong style={{ color: 'var(--text-secondary)' }}>
                {skipped.length} file{skipped.length === 1 ? '' : 's'} not added:
              </strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {skipped.map((s, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>
                    {s.filename} — {s.status === 'duplicate' ? 'already on this job'
                      : s.status === 'restored' ? 'restored from archive'
                        : s.error || s.status}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="modal-actions" style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <button type="button" className="btn" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={() => {
              setTouched(true);
              if (blocking) return;
              onConfirm(edited);
            }}
          >
            {saving
              ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Scoring…</>
              : <><Check size={14} strokeWidth={2.2} /> Confirm &amp; score</>}
          </button>
        </div>

        {touched && blocking && (
          <div style={{ fontSize: 12, color: 'var(--red)', textAlign: 'right', marginTop: 6 }}>
            Fix the highlighted fields to continue.
          </div>
        )}
      </div>
    </div>
  );
}
